import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
// Coordinate Grid - System coordinates, not decoration
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.02] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '60px 60px'
}} aria-hidden="true" data-id="element-1293" />;
// 1. HERO
const HeroSection = () => <section className="relative min-h-screen flex flex-col justify-center px-6 pt-20 pb-12 overflow-hidden bg-[#0A0A0A]" data-id="element-1294">
    <CoordinateGrid data-id="element-1295" />

    <div className="relative z-10 max-w-5xl mx-auto space-y-12" data-id="element-1296">
      <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif leading-[1.15] text-[#F5F5F5] tracking-tight" style={{
      fontFamily: 'Georgia, serif'
    }} data-id="element-1297">
        See the pattern you keep repeating.
      </h1>

      <div className="space-y-6 max-w-3xl" data-id="element-1298">
        <p className="text-xl md:text-2xl text-[#F5F5F5] leading-relaxed font-light" data-id="element-1299">
          Diagnostics are one-time, deep pattern reports. They are generated
          from how you answer a short set of situational questions, and they
          reveal the underlying structure behind how you think, decide, and act
          — in specific areas of your life and work.
        </p>
      </div>

      <div className="pt-8 flex flex-col sm:flex-row gap-4" data-id="element-1300">
        <a href="#diagnostics" data-id="element-1301">
          <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-8 py-6 text-sm uppercase tracking-widest font-medium" data-id="element-1302">
            Explore the diagnostics
          </Button>
        </a>
      </div>

      <div className="pt-4" data-id="element-1303">
        <p className="text-sm text-gray-600" data-id="element-1304">
          Powered by Decoder — our pattern recognition engine.
        </p>
      </div>
    </div>
  </section>;
// 2. WHAT THESE ARE
const WhatTheseAreSection = () => <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1305">
    <div className="max-w-4xl mx-auto" data-id="element-1306">
      <div className="space-y-12" data-id="element-1307">
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1308">
          These are not personality tests.
        </h2>

        <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-1309">
          <p data-id="element-1310">Diagnostics are not about traits, types, or labels.</p>

          <p data-id="element-1311">
            They do not tell you who you are. They show you the structure you
            operate from.
          </p>

          <p data-id="element-1312">
            Each diagnostic is generated from your answers to a small set of
            carefully designed situational questions.
          </p>

          <div className="pl-6 border-l-2 border-[#2B2B2B] space-y-4 pt-6" data-id="element-1313">
            <p className="text-gray-500" data-id="element-1314">Think of them as:</p>
            <p data-id="element-1315">• An X-ray, not a story</p>
            <p data-id="element-1316">• A structural map, not advice</p>
            <p data-id="element-1317">• A mirror, not a prescription</p>
          </div>
        </div>
      </div>
    </div>
  </section>;
// 3. WHAT YOU GET
const WhatYouGetSection = () => <section className="py-32 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-1318">
    <div className="max-w-6xl mx-auto" data-id="element-1319">
      <div className="space-y-16" data-id="element-1320">
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1321">
          What a diagnostic gives you
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1322">
          <div className="space-y-4" data-id="element-1323">
            <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1324">
              A clear structural pattern
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1325">
              Not what happened. Not how you feel about it. But the repeating
              shape behind your decisions and reactions.
            </p>
          </div>

          <div className="space-y-4" data-id="element-1326">
            <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1327">
              The tradeoffs of that pattern
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1328">
              Every pattern optimizes for something. Every pattern also has a
              cost. You see both, without judgment.
            </p>
          </div>

          <div className="space-y-4" data-id="element-1329">
            <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1330">
              The loop you keep running
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1331">
              The same structure shows up across situations. A diagnostic makes
              that loop visible in one place.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>;
// 4. THE DIAGNOSTICS
const DiagnosticsSection = () => <section id="diagnostics" className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1332">
    <div className="max-w-6xl mx-auto" data-id="element-1333">
      <div className="space-y-16" data-id="element-1334">
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1335">
          The available diagnostics
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1336">
          {/* SELF DIAGNOSTIC */}
          <div className="border border-[#2B2B2B] p-10 space-y-6 bg-[#0A0A0A]" data-id="element-1337">
            <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-1338">
              Self Diagnostic
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1339">
              Reveals the core structure behind how you relate to yourself,
              pressure, control, and uncertainty.
              <br data-id="element-1340" />
              <br data-id="element-1341" />
              This is the deepest and most general snapshot of your operating
              pattern.
            </p>
            <div className="pt-4" data-id="element-1342">
              <Link to="/reports/self" data-id="element-1343">
                <Button className="bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-6 py-4 text-sm font-medium w-full" data-id="element-1344">
                  View Self Diagnostic
                </Button>
              </Link>
            </div>
          </div>

          {/* WORK DIAGNOSTIC */}
          <div className="border border-[#2B2B2B] p-10 space-y-6 bg-[#0A0A0A]" data-id="element-1345">
            <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-1346">
              Work Diagnostic
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1347">
              Reveals the structure behind how you operate in roles, decisions,
              authority, conflict, and responsibility.
              <br data-id="element-1348" />
              <br data-id="element-1349" />
              This is a snapshot of your work pattern.
            </p>
            <div className="pt-4" data-id="element-1350">
              <Link to="/reports/work" data-id="element-1351">
                <Button className="bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-6 py-4 text-sm font-medium w-full" data-id="element-1352">
                  View Work Diagnostic
                </Button>
              </Link>
            </div>
          </div>

          {/* RELATIONSHIP DIAGNOSTIC */}
          <div className="border border-[#2B2B2B] p-10 space-y-6 bg-[#0A0A0A]" data-id="element-1353">
            <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-1354">
              Relationship Diagnostic
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1355">
              Reveals the structure behind how you move in closeness, distance,
              conflict, and emotional negotiation.
              <br data-id="element-1356" />
              <br data-id="element-1357" />
              This is a snapshot of your relational pattern.
            </p>
            <div className="pt-4" data-id="element-1358">
              <Link to="/reports/relationship" data-id="element-1359">
                <Button className="bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-6 py-4 text-sm font-medium w-full" data-id="element-1360">
                  View Relationship Diagnostic
                </Button>
              </Link>
            </div>
          </div>

          {/* CHILD DIAGNOSTIC */}
          <div className="border border-[#2B2B2B] p-10 space-y-6 bg-[#0A0A0A]" data-id="element-1361">
            <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-1362">
              Child Diagnostic
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1363">
              Reveals the core operating structure of a child — how they deal
              with pressure, novelty, boundaries, and authority.
              <br data-id="element-1364" />
              <br data-id="element-1365" />
              This is a snapshot of their learning and adaptation pattern.
            </p>
            <div className="pt-4" data-id="element-1366">
              <Link to="/reports/child" data-id="element-1367">
                <Button className="bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-6 py-4 text-sm font-medium w-full" data-id="element-1368">
                  View Child Diagnostic
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>;
// 5. HOW THIS IS DIFFERENT
const HowThisIsDifferentSection = () => <section className="py-32 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-1369">
    <div className="max-w-6xl mx-auto" data-id="element-1370">
      <div className="space-y-16" data-id="element-1371">
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5] text-center" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1372">
          This is not self-help.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1373">
          <div className="space-y-4 text-center" data-id="element-1374">
            <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1375">Not Therapy</h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1376">
              We don't work with emotions or stories. We work with structure.
            </p>
          </div>

          <div className="space-y-4 text-center" data-id="element-1377">
            <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1378">Not Coaching</h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1379">
              We don't guide or suggest. We reflect what is already there.
            </p>
          </div>

          <div className="space-y-4 text-center" data-id="element-1380">
            <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-1381">Not Advice</h3>
            <p className="text-base text-gray-400 leading-relaxed" data-id="element-1382">
              We don't tell you what to do. We show you what you keep doing.
            </p>
          </div>
        </div>

        <div className="pt-8 text-center" data-id="element-1383">
          <p className="text-lg text-[#F5F5F5] leading-relaxed" data-id="element-1384">
            The diagnostic does not change you. It makes the pattern visible.
          </p>
        </div>
      </div>
    </div>
  </section>;
// 6. HOW PEOPLE USE DIAGNOSTICS
const HowPeopleUseSection = () => <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1385">
    <div className="max-w-6xl mx-auto" data-id="element-1386">
      <div className="space-y-16" data-id="element-1387">
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1388">
          When people use these
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-1389">
          <div className="space-y-6" data-id="element-1390">
            <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest" data-id="element-1391">
              Before a Transition
            </p>
            <p className="text-lg text-gray-400 leading-relaxed" data-id="element-1392">
              Career change, relationship change, major life decision.
            </p>
          </div>

          <div className="space-y-6" data-id="element-1393">
            <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest" data-id="element-1394">
              When Something Feels Stuck
            </p>
            <p className="text-lg text-gray-400 leading-relaxed" data-id="element-1395">
              Repeating conflicts. Repeating outcomes. Repeating frustration.
            </p>
          </div>

          <div className="space-y-6" data-id="element-1396">
            <p className="text-xs font-mono text-[#64748B] uppercase tracking-widest" data-id="element-1397">
              Out of Pure Clarity
            </p>
            <p className="text-lg text-gray-400 leading-relaxed" data-id="element-1398">
              To see their own structure without drama or narrative.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>;
// 7. SNAPSHOT VS MIRROR
const SnapshotVsMirrorSection = () => <section className="py-32 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-1399">
    <div className="max-w-4xl mx-auto" data-id="element-1400">
      <div className="space-y-12" data-id="element-1401">
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1402">
          A snapshot — not a movie
        </h2>

        <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-1403">
          <p data-id="element-1404">A diagnostic is a still image of your pattern.</p>

          <p data-id="element-1405">
            If you want to see this pattern playing out in real life, over time,
            that's what Work Mirror is for.
          </p>

          <div className="pl-6 border-l-2 border-[#2B2B2B] space-y-4 pt-6" data-id="element-1406">
            <p data-id="element-1407">Diagnostics show you the structure.</p>
            <p data-id="element-1408">Work Mirror lets you watch it in motion.</p>
          </div>
        </div>

        <div className="pt-8" data-id="element-1409">
          <Link to="/work-mirror" data-id="element-1410">
            <Button className="bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-8 py-4 text-sm font-medium" data-id="element-1411">
              Explore Work Mirror
            </Button>
          </Link>
        </div>
      </div>
    </div>
  </section>;
// 8. THE ENGINE
const EngineSection = () => <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1412">
    <div className="max-w-4xl mx-auto" data-id="element-1413">
      <div className="space-y-12" data-id="element-1414">
        <h2 className="text-3xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
        fontFamily: 'Georgia, serif'
      }} data-id="element-1415">
          Powered by Decoder
        </h2>

        <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-1416">
          <p data-id="element-1417">
            Every diagnostic is generated by Decoder — our pattern recognition
            engine.
          </p>

          <div className="pl-6 border-l-2 border-[#2B2B2B] space-y-4" data-id="element-1418">
            <p className="text-gray-500" data-id="element-1419">The same engine powers:</p>
            <p data-id="element-1420">• Diagnostics (snapshots)</p>
            <p data-id="element-1421">• Work Mirror (living system)</p>
          </div>

          <p className="pt-6 border-t border-[#2B2B2B] text-[#F5F5F5]" data-id="element-1422">
            This is not content.
            <br data-id="element-1423" />
            This is not templated advice.
            <br data-id="element-1424" />
            This is a system detecting structure.
          </p>
        </div>

        <div className="pt-8" data-id="element-1425">
          <Link to="/decoder" className="inline-flex items-center gap-3 text-base text-[#F5F5F5] hover:text-gray-400 transition-colors group" data-id="element-1426">
            <span data-id="element-1427">Learn about Decoder</span>
            <span className="text-gray-600 group-hover:text-gray-400 transition-colors" data-id="element-1428">
              →
            </span>
          </Link>
        </div>
      </div>
    </div>
  </section>;
// 9. FINAL CTA
const FinalCTASection = () => <section className="py-32 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1429">
    <div className="max-w-4xl mx-auto text-center space-y-12" data-id="element-1430">
      <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] leading-tight" style={{
      fontFamily: 'Georgia, serif'
    }} data-id="element-1431">
        Choose the area you want to see clearly.
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto" data-id="element-1432">
        <Link to="/reports/self" data-id="element-1433">
          <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-8 py-5 text-sm font-medium w-full" data-id="element-1434">
            Self Diagnostic
          </Button>
        </Link>
        <Link to="/reports/work" data-id="element-1435">
          <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-8 py-5 text-sm font-medium w-full" data-id="element-1436">
            Work Diagnostic
          </Button>
        </Link>
        <Link to="/reports/relationship" data-id="element-1437">
          <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-8 py-5 text-sm font-medium w-full" data-id="element-1438">
            Relationship Diagnostic
          </Button>
        </Link>
        <Link to="/reports/child" data-id="element-1439">
          <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-8 py-5 text-sm font-medium w-full" data-id="element-1440">
            Child Diagnostic
          </Button>
        </Link>
      </div>
    </div>
  </section>;
// FOOTER
const Footer = () => <footer className="py-12 px-6 bg-[#0A0A0A] border-t border-[#2B2B2B]" data-id="element-1441">
    <div className="max-w-6xl mx-auto" data-id="element-1442">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-1443">
        <div data-id="element-1444">
          <p className="text-sm text-gray-500 font-mono" data-id="element-1445">Lazy Monk Labs</p>
          <p className="text-xs text-gray-600 mt-1" data-id="element-1446">
            Pattern recognition systems
          </p>
        </div>

        <div className="flex gap-8 text-sm text-gray-500" data-id="element-1447">
          <Link to="/diagnostics" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1448">
            Diagnostics
          </Link>
          <Link to="/work-mirror" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1449">
            Work Mirror
          </Link>
          <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1450">
            Decoder
          </Link>
          <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1451">
            Careers
          </Link>
          <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-1452">
            Contact
          </a>
        </div>

        <div className="text-xs text-gray-600 font-mono" data-id="element-1453">
          © {new Date().getFullYear()}
        </div>
      </div>
    </div>
  </footer>;
// MAIN COMPONENT
export function DiagnosticsLanding() {
  return <>
      <Helmet data-id="element-1454">
        <title data-id="element-1455">Diagnostics | Lazy Monk Labs</title>
        <meta name="description" content="One-time, deep pattern reports. Generated from situational questions. Reveal the structure behind how you think, decide, and act." data-id="element-1456" />
      </Helmet>

      <main className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] selection:bg-[#64748B] selection:text-white" data-id="element-1457">
        {/* Navigation */}
        <header data-id="element-1458">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-1459">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-1460">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-1461">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-1462" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-1463">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-1464">
                <Link to="/diagnostics" className="text-sm text-[#F5F5F5] hover:text-gray-400 transition-colors font-medium" data-id="element-1465">
                  Diagnostics
                </Link>
                <Link to="/work-mirror" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-medium" data-id="element-1466">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-1467">
                  Decoder
                </Link>
                <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-1468">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" aria-label="Contact" data-id="element-1469">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <HeroSection data-id="element-1470" />
        <WhatTheseAreSection data-id="element-1471" />
        <WhatYouGetSection data-id="element-1472" />
        <DiagnosticsSection data-id="element-1473" />
        <HowThisIsDifferentSection data-id="element-1474" />
        <HowPeopleUseSection data-id="element-1475" />
        <SnapshotVsMirrorSection data-id="element-1476" />
        <EngineSection data-id="element-1477" />
        <FinalCTASection data-id="element-1478" />
        <Footer data-id="element-1479" />
      </main>
    </>;
}